import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container } from "@/components/container";
import { CompanyContent } from "./";
import { Navbar, NavbarActions } from "@/partials/navbar";
import { NavbarDropdown } from "./blocks/NavbarDropdown";
import { useParams } from "react-router-dom";
import axios from "axios";
import { KeenIcon } from "@/components";
import { PageMenu } from "./menu/PageMenu";
import { CompanyDetailsData } from "@/pages/buddy/types/CompanyDetailsData";
import { Loading } from "@/layouts/buddy/loading";
import { enqueueSnackbar } from "notistack";
import { CompanyModal } from "@/pages/buddy/company/blocks/source/CompanyModal";
import { JobModal } from "@/pages/buddy/job/blocks/source/JobModal"; // Ensure you have this modal available
import { CompanyProfileHero } from "@/pages/buddy/company/hero/CompanyPofileHero";

const CompanyPage = () => {
    const { p_id } = useParams(); // Access company ID from the URL
    const [company, setCompany] = useState<CompanyDetailsData | null>(null);

    // Modal state for adding a new company source
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const handleCompanyModalOpen = () => setCompanyModalOpen(true);
    const handleCompanyModalClose = () => {
        setCompanyModalOpen(false);
        // Optionally, refresh company data here if needed
    };

    // Modal state for adding a new job under this company
    const [jobModalOpen, setJobModalOpen] = useState(false);
    const handleJobModalOpen = () => setJobModalOpen(true);
    const handleJobModalClose = () => setJobModalOpen(false);

    const [loading, setLoading] = useState(true);
    const { tab } = useParams<{ tab?: string }>();
    const [activePage, setActivePage] = useState<string>(tab || "overview");
    const [noActiveJobs, setNoActiveJobs] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState(0); // Forces data refresh

    const API_COMPANY = `${import.meta.env.VITE_APP_API_URL}/company`;

    const triggerRefresh = () => setRefreshKey(prev => prev + 1);

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const response = await axios.get<CompanyDetailsData>(`${API_COMPANY}/${p_id}`, {
                headers: { Accept: "application/json" },
            });
            console.log("Fetched company data:", response.data);
            setCompany(response.data);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch company data: ${err}`, {
                variant: "solid",
                state: "danger",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyData();
    }, [p_id, refreshKey]);

    const handleSummaryFetched = useCallback((activeJobs: number) => {
        setNoActiveJobs(activeJobs === 0);
    }, []);

    if (loading) return <Loading loading={loading} />;
    if (!company) return <div>No company data available</div>;

    return (
        <Fragment>
            <CompanyProfileHero company={company} />

            {/* Modal for adding a new source to the company */}
            <CompanyModal
                mode="addSource"
                open={companyModalOpen}
                onClose={handleCompanyModalClose}
                company={company}
                refreshCompanyData={handleCompanyModalClose} // Use the same close handler to refresh data if needed
            />

            {/* Modal for adding a new job under the company */}
            <JobModal
                mode="create"
                open={jobModalOpen}
                onClose={handleJobModalClose}
                preselectedCompany={company} // Pass the company as the preselected parent for the new job
            />

            <Container>
                <Navbar>
                    <PageMenu setActivePage={setActivePage} />
                    <NavbarActions>
                        {/* Button for adding a new source */}
                        <button
                            onClick={handleCompanyModalOpen}
                            type="button"
                            className="btn btn-sm btn-primary ml-2"
                        >
                            <KeenIcon icon="plus" /> Add New Source
                        </button>
                        {/* Button for adding a new job */}
                        <button
                            onClick={handleJobModalOpen}
                            type="button"
                            className={`btn btn-sm btn-primary ${noActiveJobs ? "pulsating" : ""}`}
                        >
                            <KeenIcon icon="plus" /> Add New Job
                        </button>
                        {/*<NavbarDropdown />*/}
                    </NavbarActions>
                </Navbar>
            </Container>

            <Container>
                <CompanyContent
                    company={company}
                    activePage={activePage}
                    loading={loading}
                    setActivePage={setActivePage}
                    handleSummaryFetched={handleSummaryFetched}
                    onCpsUpdateComplete={triggerRefresh}
                />
            </Container>
        </Fragment>
    );
};

export { CompanyPage };
